import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { ArrowLeft, CreditCard, ShieldCheck } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { getStoreId } from '../utils/storage';
import { apiFetch } from '../utils/api';
import { toastSuccess, toastError, toastInfo } from '../utils/toast';
import Loading from './Loading';
import ConfirmModal from './ConfirmModal';
import StatusBadge, { accountTone } from './StatusBadge';

function priceOf(plan) {
  return parseFloat(plan?.discounted_price ?? plan?.actual_price ?? plan?.price ?? 0) || 0;
}

function actualPriceOf(plan) {
  return parseFloat(plan?.actual_price ?? 0) || 0;
}

/** Prefer API discount_percentage; else derive from actual vs discounted. */
function discountPct(plan) {
  if (!plan) return 0;
  if (plan.discount_percentage) {
    const parsed = parseInt(String(plan.discount_percentage).replace('%', ''), 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  const actual = actualPriceOf(plan);
  const discounted = priceOf(plan);
  if (actual <= 0 || discounted >= actual) return 0;
  return Math.round(((actual - discounted) / actual) * 100);
}

export default function Upgrade({ onGoVault }) {
  const { t } = useTranslation();
  const hashSiteId = getStoreId();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [chargeLoading, setChargeLoading] = useState(false);
  const [noPaymentOption, setNoPaymentOption] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [continueLoading, setContinueLoading] = useState(false);

  const productOptions = useMemo(() => {
    const counts = new Set();
    for (const s of subscriptions) {
      const n = s.no_of_products ?? s.credits;
      if (n != null) counts.add(String(n));
    }
    return Array.from(counts).sort((a, b) => Number(a) - Number(b));
  }, [subscriptions]);

  const plansForCount = useMemo(() => {
    if (!selectedProducts) return [];
    return subscriptions.filter(
      (s) => String(s.no_of_products ?? s.credits) === String(selectedProducts),
    );
  }, [subscriptions, selectedProducts]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [pricing, sub, pay] = await Promise.all([
          apiFetch('/api/pricing', {}, { id: hashSiteId }),
          apiFetch('/api/subscription', {}, { id: hashSiteId }).catch(() => ({
            subscription: null,
          })),
          apiFetch('/api/ecwid-billing/payment-status', {}, { id: hashSiteId }).catch(() => ({
            hasPaymentOption: true,
          })),
        ]);
        setSubscriptions(pricing.subscriptions || []);
        setCurrentSubscription(sub.subscription || null);
        setNoPaymentOption(pay.hasPaymentOption === false);
        const first = pricing.subscriptions?.[0];
        if (first) {
          setSelectedProducts(String(first.no_of_products ?? first.credits ?? ''));
        }
      } catch (err) {
        toastError(err, t('upgrade.toasts.couldNotLoadPricing'), 'pricing');
      } finally {
        setLoading(false);
      }
    }
    if (hashSiteId) load();
  }, [hashSiteId, t]);

  useEffect(() => {
    if (plansForCount.length === 1) setSelectedPlan(plansForCount[0]);
    else if (plansForCount.length > 0) {
      const monthly = plansForCount.find(
        (p) => String(p.subscription_type || '').toUpperCase() === 'MONTHLY',
      );
      setSelectedPlan(monthly || plansForCount[0]);
    } else {
      setSelectedPlan(null);
    }
  }, [plansForCount]);

  function askCharge() {
    if (!selectedPlan) return;
    if (noPaymentOption) {
      toastInfo(t('upgrade.toasts.addPaymentFirst'));
      return;
    }
    setShowChargeModal(true);
  }

  async function handleCharge() {
    if (!selectedPlan) return;
    setChargeLoading(true);
    try {
      const amount = priceOf(selectedPlan);
      const sku = selectedPlan.sku || selectedPlan.code || selectedPlan.subscription_code;
      const result = await apiFetch(
        '/api/ecwid-billing/charge',
        {
          method: 'POST',
          body: JSON.stringify({
            amount,
            currency: selectedPlan.currency || 'USD',
            description: `Backuply ${sku}`,
            idempotencyKey: crypto.randomUUID(),
            metadata: { subscription_code: sku },
          }),
        },
        { id: hashSiteId },
      );
      toastSuccess(t('upgrade.toasts.activated'));
      setCurrentSubscription({
        subscription_id: result.transactionId,
        subscription_name: sku,
        status: 'ACTIVE',
        sku,
      });
      setShowChargeModal(false);
    } catch (err) {
      toastError(err, t('upgrade.toasts.paymentFailed'), 'charge');
    } finally {
      setChargeLoading(false);
    }
  }

  async function handleCancel() {
    setCancelLoading(true);
    try {
      await apiFetch(
        '/api/subscription/cancel',
        {
          method: 'POST',
          body: JSON.stringify({
            subscription_id: currentSubscription?.subscription_id || null,
          }),
        },
        { id: hashSiteId },
      );
      toastSuccess(t('upgrade.toasts.cancelled'));
      setCurrentSubscription(null);
      setShowCancelModal(false);
    } catch (err) {
      toastError(err, t('upgrade.toasts.couldNotCancel'), 'cancel');
    } finally {
      setCancelLoading(false);
    }
  }

  async function handleContinue() {
    setContinueLoading(true);
    try {
      await apiFetch(
        '/api/subscription/continue',
        {
          method: 'POST',
          body: JSON.stringify({
            subscription_id: currentSubscription?.subscription_id || null,
          }),
        },
        { id: hashSiteId },
      );
      toastSuccess(t('upgrade.toasts.continued'));
    } catch (err) {
      toastError(err, t('upgrade.toasts.couldNotContinue'), 'continue');
    } finally {
      setContinueLoading(false);
    }
  }

  if (loading) return <Loading message={t('upgrade.loading')} />;

  const selectedSku = selectedPlan?.sku || selectedPlan?.code;
  const selectedPrice = priceOf(selectedPlan);
  const amountFixed = selectedPrice.toFixed(2);

  return (
    <div className="upgrade-shell">
      <section className="vault-dashboard">
        <div className="vault-dashboard-head">
          <div>
            <div className="vault-eyebrow">
              <CreditCard size={14} /> {t('upgrade.billing')}
            </div>
            <h1 className="vault-title">{t('upgrade.title')}</h1>
            <p className="upgrade-lede">
              {t('upgrade.ledeBefore')} <strong>{t('upgrade.ledeStrong')}</strong>{' '}
              {t('upgrade.ledeAfter')}
            </p>
          </div>
          {onGoVault ? (
            <Button variant="outline-secondary" className="upgrade-back" onClick={onGoVault}>
              <ArrowLeft size={16} className="me-1" /> {t('upgrade.backToVault')}
            </Button>
          ) : null}
        </div>
      </section>

      {noPaymentOption ? (
        <div className="vault-banner warn">
          <div className="vault-banner-title">{t('upgrade.paymentRequiredTitle')}</div>
          <p>
            {t('upgrade.paymentRequiredBodyBefore')}{' '}
            <strong>{t('upgrade.paymentRequiredBodyStrong')}</strong>{' '}
            {t('upgrade.paymentRequiredBodyAfter')}
          </p>
        </div>
      ) : null}

      {currentSubscription ? (
        <section className="upgrade-current">
          <div>
            <div className="upgrade-current-label">{t('upgrade.currentPlan')}</div>
            <div className="upgrade-current-name">
              {currentSubscription.subscription_name ||
                currentSubscription.sku ||
                t('upgrade.defaultPlanName')}
              <StatusBadge tone={accountTone(currentSubscription.status || 'paid')}>
                {currentSubscription.status || 'ACTIVE'}
              </StatusBadge>
            </div>
            {currentSubscription.next_billing_date ? (
              <div className="text-body-secondary small mt-1">
                {t('upgrade.nextBilling', {
                  date: String(currentSubscription.next_billing_date),
                })}
              </div>
            ) : null}
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="outline-danger" size="sm" onClick={() => setShowCancelModal(true)}>
              {t('upgrade.cancelPlan')}
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              disabled={continueLoading}
              onClick={handleContinue}
            >
              {continueLoading ? <Spinner size="sm" /> : t('upgrade.continueBilling')}
            </Button>
          </div>
        </section>
      ) : null}

      <section className="upgrade-plans">
        <div className="upgrade-plans-head">
          <h2>{t('upgrade.choosePlan')}</h2>
          <p>{t('upgrade.choosePlanHint')}</p>
        </div>

        {subscriptions.length === 0 ? (
          <div className="vault-empty">
            <div className="vault-empty-icon">
              <ShieldCheck size={24} />
            </div>
            <h3>{t('upgrade.plansUnavailableTitle')}</h3>
            <p>{t('upgrade.plansUnavailableBody')}</p>
          </div>
        ) : (
          <>
            <Form.Group className="mb-3" style={{ maxWidth: 280 }}>
              <Form.Label className="fw-semibold">{t('upgrade.productCount')}</Form.Label>
              <Form.Select
                value={selectedProducts}
                onChange={(e) => setSelectedProducts(e.target.value)}
              >
                {productOptions.map((n) => (
                  <option key={n} value={n}>
                    {t('upgrade.upToProducts', { count: Number(n).toLocaleString() })}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <div className="plan-cards">
              {plansForCount.map((plan) => {
                const sku = plan.sku || plan.code;
                const active = selectedSku === sku;
                const savePct = discountPct(plan);
                const listPrice = actualPriceOf(plan);
                const salePrice = priceOf(plan);
                const cycle = String(plan.subscription_type || 'Plan').toUpperCase();
                return (
                  <button
                    key={sku}
                    type="button"
                    className={`plan-card ${active ? 'active' : ''}`}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    <div className="plan-card-cycle">
                      <span>{cycle}</span>
                      {savePct > 0 ? (
                        <span className="plan-card-save">
                          {t('upgrade.savePct', { pct: savePct })}
                        </span>
                      ) : null}
                    </div>
                    <div className="plan-card-price">
                      {savePct > 0 && listPrice > salePrice ? (
                        <span className="plan-card-strike">${listPrice.toFixed(2)}</span>
                      ) : null}
                      ${salePrice.toFixed(2)}
                      <span className="plan-card-period">
                        {cycle === 'YEARLY'
                          ? t('upgrade.perYear')
                          : cycle === 'MONTHLY'
                            ? t('upgrade.perMonth')
                            : ''}
                      </span>
                    </div>
                    <div className="plan-card-meta">
                      {t('upgrade.upToProducts', {
                        count: Number(plan.no_of_products ?? plan.credits).toLocaleString(),
                      })}
                    </div>
                    {active ? (
                      <StatusBadge tone="primary">{t('upgrade.selected')}</StatusBadge>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <Button
              className="btn-vault-primary mt-3"
              disabled={!selectedPlan || chargeLoading || noPaymentOption}
              onClick={askCharge}
            >
              {t('upgrade.payWithEcwid', { amount: amountFixed })}
            </Button>
          </>
        )}
      </section>

      <ConfirmModal
        show={showChargeModal}
        title={t('upgrade.confirmPaymentTitle')}
        body={
          <p className="mb-0">
            <Trans
              i18nKey="upgrade.confirmPaymentBody"
              values={{ amount: amountFixed, sku: selectedSku }}
              components={{ strong: <strong /> }}
            />
          </p>
        }
        confirmLabel={t('upgrade.payAmount', { amount: amountFixed })}
        variant="primary"
        loading={chargeLoading}
        onCancel={() => !chargeLoading && setShowChargeModal(false)}
        onConfirm={handleCharge}
      />

      <ConfirmModal
        show={showCancelModal}
        title={t('upgrade.cancelSubscriptionTitle')}
        body={<p className="mb-0">{t('upgrade.cancelSubscriptionBody')}</p>}
        confirmLabel={t('upgrade.cancelSubscriptionConfirm')}
        cancelLabel={t('upgrade.keepPlan')}
        variant="danger"
        loading={cancelLoading}
        onCancel={() => !cancelLoading && setShowCancelModal(false)}
        onConfirm={handleCancel}
      />
    </div>
  );
}
